import { useMutation, useQuery } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import { acceptInvitation, fetchInvitation } from '@/features/access/api'
import type { ApiError } from '@/lib/api/client'

/**
 * The page a client lands on from an invitation link. Public: they have no
 * account yet, so the single-use token is the only credential.
 */
export function AcceptInvitePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token = '' } = useParams()
  const [name, setName] = useState('')

  const invitation = useQuery<{ email: string; expiresAt: string }, ApiError>({
    queryKey: ['invitation', token],
    queryFn: () => fetchInvitation(token),
    retry: false,
  })

  const accept = useMutation<void, ApiError, void>({
    mutationFn: () => acceptInvitation(token, name),
    onSuccess: () => navigate('/portfolio', { replace: true }),
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    accept.mutate()
  }

  return (
    <div className="grid min-h-dvh place-items-center px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <img src="/awj-systems-logo-v.svg" alt="" className="h-8 w-auto" />
          <div className="leading-tight">
            <div className="text-ink-900 font-semibold">{t('app.name')}</div>
            <div className="text-ink-500 text-xs">{t('app.org')}</div>
          </div>
        </div>

        <div className="border-line bg-surface rounded-lg border p-6">
          {invitation.isPending ? (
            <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
          ) : invitation.isError ? (
            <>
              <h1 className="text-ink-900 mb-2 text-lg font-semibold">
                {t('access.inviteInvalidTitle')}
              </h1>
              <p className="text-ink-700 text-sm">{t('access.inviteInvalidBody')}</p>
            </>
          ) : (
            <form onSubmit={submit}>
              <h1 className="text-ink-900 mb-1 text-lg font-semibold">{t('access.inviteTitle')}</h1>
              <p className="text-ink-500 mb-5 text-sm">{invitation.data.email}</p>

              <label className="mb-5 block">
                <span className="text-ink-700 mb-1.5 block text-sm font-medium">
                  {t('access.yourName')}
                </span>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="border-line focus:border-brand-600 w-full rounded-md border px-3 py-2 text-sm outline-none"
                />
              </label>

              <button
                type="submit"
                disabled={accept.isPending}
                className="bg-brand-600 hover:bg-brand-700 w-full rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                {t('access.acceptInvite')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
